// ============================================================================
// COMPLIANCE USER - MY UPLOADS PAGE
// ============================================================================
// Purpose: View all evidence uploaded by the current user
// Mental model: "Show me all the evidence I've uploaded."
// ============================================================================

import React, { useState, useEffect } from 'react';
import { useAuth } from '@contexts/AuthContext';
import {
  FileText,
  Download,
  Trash2,
  RefreshCw,
  AlertTriangle,
  CheckSquare,
  Eye,
  Search
} from 'lucide-react';
import { complianceExecutionApi, EvidenceItem } from '@domains/compliance/execution/api/complianceExecution.api';
import PageInfoButton from '@/components/PageInfoButton';

const MyUploads: React.FC = () => {
  const { hasPermission, user } = useAuth();
  const [evidence, setEvidence] = useState<EvidenceItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadEvidence();
  }, []);

  const loadEvidence = async () => {
    setIsLoading(true);
    try {
      // Get all assigned pages first
      const pages = await complianceExecutionApi.getAssignedPages();

      // Get evidence for each page
      const allEvidence: EvidenceItem[] = [];
      for (const page of pages) {
        const pageEvidence = await complianceExecutionApi.getEvidenceItems(page.id);
        // Filter to only show evidence uploaded by current user
        const userEvidence = pageEvidence.filter(e => e.uploaded_by === user?.id);
        allEvidence.push(...userEvidence);
      }

      setEvidence(allEvidence);
    } catch (error) {
      console.error('Error loading evidence:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (evidenceId: number) => {
    if (!confirm('Are you sure you want to delete this evidence?')) {
      return;
    }

    try {
      await complianceExecutionApi.deleteEvidence(evidenceId);
      await loadEvidence();
    } catch (error) {
      console.error('Error deleting evidence:', error);
      alert('Failed to delete evidence');
    }
  };

  const handleDownload = (fileUrl: string, fileName: string) => {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileName;
    link.click();
  };

  const handlePreviewEvidence = async (evidenceId: number) => {
    try {
      // Use the proper API endpoint for authenticated file preview
      const blob = await complianceExecutionApi.downloadEvidence(evidenceId);

      // Create a temporary URL for preview
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error previewing evidence:', error);
    }
  };

  const isPreviewable = (mimeType: string) => {
    // File types that can be previewed in browser
    const previewableTypes = [
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'text/plain',
      'text/html'
    ];
    return previewableTypes.includes(mimeType);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'submitted':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md font-bold text-[10px] text-green-700 bg-green-100">
          <CheckSquare size={12} />
          Submitted
        </span>;
      case 'draft':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md font-bold text-[10px] text-slate-600 bg-slate-100">
          Draft
        </span>;
      default:
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md font-bold text-[10px] text-slate-600 bg-slate-100">
          {status}
        </span>;
    }
  };

  const getFileIcon = (_fileType: string) => {
    return <FileText size={18} className="text-slate-400" />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getStats = () => {
    const total = evidence.length;
    const submitted = evidence.filter(e => e.status === 'submitted').length;
    const draft = evidence.filter(e => e.status === 'draft').length;

    return { total, submitted, draft };
  };

  const stats = getStats();
  const filteredEvidence = evidence.filter(item =>
    item.file_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.notes?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!hasPermission('compliance.read')) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-3xl border border-slate-100 p-8">
          <AlertTriangle className="text-red-500 mb-3" size={36} />
          <h1 className="text-xl font-bold text-slate-900 mb-2">Access Denied</h1>
          <p className="text-slate-500 text-sm">You don't have permission to view evidence.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="text-blue-600 animate-spin" size={40} />
          <p className="text-slate-500 text-sm">Loading uploads...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900">My Uploads</h1><PageInfoButton /></div>
            <p className="text-slate-500 text-sm">All evidence you have uploaded</p>
          </div>
          <button
            onClick={loadEvidence}
            className="px-4 py-2.5 bg-white border border-slate-200 text-slate-700 font-medium text-sm rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-2"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>

        {/* Search */}
        <div className="bg-white rounded-3xl border border-slate-100 p-4 mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <input
              type="text"
              placeholder="Search uploads by file name or notes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            />
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
            <p className="text-xs font-medium text-slate-500 mb-1">Total Uploads</p>
            <p className="text-xl font-bold text-slate-900">{stats.total}</p>
          </div>
          <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
            <p className="text-xs font-medium text-slate-500 mb-1">Submitted</p>
            <p className="text-xl font-bold text-slate-900">{stats.submitted}</p>
          </div>
          <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
            <p className="text-xs font-medium text-slate-500 mb-1">Draft</p>
            <p className="text-xl font-bold text-slate-900">{stats.draft}</p>
          </div>
        </div>

        {/* Evidence List */}
        <div className="bg-white rounded-3xl border border-slate-100">
          <div className="px-5 py-3 border-b border-slate-100">
            <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              My Evidence Uploads
              <span className="bg-blue-600 text-white px-2 py-0.5 rounded-full text-xs font-bold">
                {filteredEvidence.length}
              </span>
            </h2>
          </div>
          <div className="p-4">
            {filteredEvidence.length === 0 ? (
              <div className="py-12">
                <FileText className="text-slate-300 mb-3" size={40} />
                <h3 className="text-base font-semibold text-slate-900 mb-1">
                  {searchTerm ? 'No uploads found' : 'No uploads yet'}
                </h3>
                <p className="text-slate-500 text-sm max-w-md mx-auto">
                  {searchTerm
                    ? 'Try adjusting your search criteria'
                    : 'You haven\'t uploaded any evidence yet. Go to your assigned pages to upload evidence.'}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredEvidence.map(item => (
                  <div
                    key={item.id}
                    className="rounded-lg border border-slate-200 p-4 hover:border-slate-300 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className="p-2 bg-slate-50 rounded-lg shrink-0">
                          {getFileIcon(item.file_type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <h3 className="font-semibold text-sm text-slate-900 truncate">{item.file_name}</h3>
                            {getStatusBadge(item.status)}
                          </div>

                          <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 mb-1">
                            <span>{formatFileSize(item.file_size)}</span>
                            <span>{new Date(item.created_at).toLocaleDateString()}</span>
                            {item.first_name && item.last_name && (
                              <span>Uploaded by {item.first_name} {item.last_name}</span>
                            )}
                          </div>

                          {item.notes && (
                            <p className="text-xs text-slate-500 bg-slate-50 rounded p-2 border border-slate-100 mt-1">
                              {item.notes}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-1.5 shrink-0">
                        {isPreviewable(item.mime_type) ? (
                          <button
                            onClick={() => handlePreviewEvidence(item.id)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Preview"
                          >
                            <Eye size={16} />
                          </button>
                        ) : (
                          <button
                            className="p-2 text-slate-300 rounded-lg cursor-not-allowed"
                            title="Preview not available for this file type. Support coming soon!"
                          >
                            <Eye size={16} />
                          </button>
                        )}
                        <button
                          onClick={() => handleDownload(item.file_url, item.file_name)}
                          className="p-2 text-slate-500 hover:bg-slate-50 rounded-lg transition-colors"
                          title="Download"
                        >
                          <Download size={16} />
                        </button>
                        {item.status === 'draft' && (
                          <button
                            onClick={() => handleDelete(item.id)}
                            className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyUploads;
